import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toggleLike as toggleLikeFB } from '../../services/firebase/likes.service';
import { RootState } from '../index';
import logger from '../../utils/logger';
import { handleFirebaseDataUpdate, updatePostLikeStatus } from './feedSlice';
import { updatePublicPostLikeStatus } from './publicFeedSlice';

// Define types for the like operation
interface LikeParams {
  contentId: string;
  contentType: string;
  like: boolean;
  userId: string;
  userProfile: {
    username?: string;
    displayName?: string;
    photoURL?: string | null;
  };
  contentOwner?: string; // Optional owner ID to avoid lookups
}

// Define the likes state structure
interface LikesState {
  likedContent: Record<string, boolean>; // Map of contentId -> liked status for local caching
  pendingLikes: Record<string, boolean>; // Map of contentId -> pending operation
  error: string | null;
}

// Define the initial state
const initialState: LikesState = {
  likedContent: {},
  pendingLikes: {},
  error: null
};

// Create an async thunk for toggling likes
export const toggleLike = createAsyncThunk<
  { contentId: string; liked: boolean },
  LikeParams,
  { state: RootState }
>(
  'likes/toggle',
  async (params, { rejectWithValue, dispatch }) => {
    const { contentId, like, userId, userProfile, contentOwner, contentType } = params;

    try {
      // Call the Firebase service with owner and contentType if provided
      await toggleLikeFB(contentId, userId, like, userProfile, contentOwner, contentType);

      // Update feed slices immediately so UI reflects the change when navigating back
      // This is the key fix for the "like doesn't show until refresh" issue
      dispatch(updatePostLikeStatus({ postId: contentId, userId, liked: like }));
      dispatch(updatePublicPostLikeStatus({ postId: contentId, userId, liked: like }));

      return {
        contentId,
        liked: like
      };
    } catch (error) {
      logger.error('Error toggling like:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to toggle like');
    }
  }
);

// Create the likes slice
const likesSlice = createSlice({
  name: 'likes',
  initialState,
  reducers: {
    // Reset the error state
    clearError: (state) => {
      state.error = null;
    },
    // Used for optimistic updates
    setContentLiked: (state, action) => {
      const { contentId, liked } = action.payload;
      state.likedContent[contentId] = liked;
    },
    // Used to track pending operations
    setPendingLike: (state, action) => {
      const { contentId, pending } = action.payload;
      if (pending) {
        state.pendingLikes[contentId] = true;
      } else {
        delete state.pendingLikes[contentId];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Handle successful like toggle
      .addCase(toggleLike.fulfilled, (state, action) => {
        const { contentId, liked } = action.payload;
        
        // Update the cache
        state.likedContent[contentId] = liked;
        
        // Remove from pending
        delete state.pendingLikes[contentId];
      })
      // Handle pending like toggle
      .addCase(toggleLike.pending, (state, action) => {
        const { contentId } = action.meta.arg;
        
        // Mark as pending
        state.pendingLikes[contentId] = true;
        
        // Optimistically update the cache
        state.likedContent[contentId] = action.meta.arg.like;
      })
      // Handle like toggle failure
      .addCase(toggleLike.rejected, (state, action) => {
        const { contentId, like } = action.meta.arg;
        
        // Revert the optimistic update
        state.likedContent[contentId] = !like;
        
        // Remove from pending
        delete state.pendingLikes[contentId];
        
        // Set the error message
        state.error = action.payload as string || 'Failed to toggle like';
      })
      // Listen to feedSlice Firebase data updates to sync like states
      .addCase(handleFirebaseDataUpdate, (state, action) => {
        const { postId, firebaseData, currentUserId } = action.payload;
        
        // Update our liked content cache from Firebase real-time data
        if (firebaseData && firebaseData.likes && currentUserId) {
          const isLiked = !!(firebaseData.likes[currentUserId]);
          state.likedContent[postId] = isLiked;
        }
      });
  }
});

// Export actions
export const { clearError, setContentLiked, setPendingLike } = likesSlice.actions;

// Export reducer
export default likesSlice.reducer;

// Create selectors
export const selectLikedContent = (state: RootState) => state.likes.likedContent;
export const selectPendingLikes = (state: RootState) => state.likes.pendingLikes;
export const selectLikesError = (state: RootState) => state.likes.error;

// Helper selector to determine if a specific content is liked
export const selectIsContentLiked = (state: RootState, contentId: string) => {
  return state.likes.likedContent[contentId] || false;
};

// Helper selector to determine if a like operation is pending for specific content
export const selectIsLikePending = (state: RootState, contentId: string) => {
  return !!state.likes.pendingLikes[contentId];
};